import React, { useEffect, useMemo, useState } from 'react';
import { InfoCircleOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import { axiosInstance } from '../../config';
import './BalanceReport.css';

const CACHE_KEY = 'dashboardBalanceCache';

const readCache = () => {
    try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
        if (!cached?.total && !cached?.cashflows) return null;
        return cached;
    } catch {
        return null;
    }
};

const moneyValue = (value) => {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value === 'string') {
        const amount = Number(value.replace(/\s/g, '').replace(',', '.'));
        return Number.isFinite(amount) ? amount : 0;
    }
    if (typeof value === 'object') {
        return moneyValue(value.value ?? value.amount ?? value.payment);
    }
    return 0;
};

const formatMoney = (value) =>
    `${Math.round(moneyValue(value)).toLocaleString('ru-RU')} ₽`;

const formatPoints = (value) => Math.round(moneyValue(value)).toLocaleString('ru-RU');

const formatDayMonth = (date) =>
    date
        .toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
        .replace('.', '');

const formatPeriod = (from, to) => {
    if (!from || !to) return 'Последние 30 дней';
    const fromDate = new Date(`${from}T00:00:00`);
    const toDate = new Date(`${to}T00:00:00`);
    return `За период ${formatDayMonth(fromDate)} — ${formatDayMonth(toDate)}`;
};

const amountDetails = (block) => {
    const details = block?.amount_details || {};
    return {
        revenue: moneyValue(details.revenue),
        points: moneyValue(details.points_for_discounts),
        partners: moneyValue(details.partner_programs),
    };
};

const extractChildren = (item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const nested = ['items', 'details', 'services', 'children', 'amount_details', 'operations']
        .map((key) => item[key])
        .find((value) => Array.isArray(value) && value.length);
    if (!nested) return [];
    return nested
        .map((child) => ({
            name: child?.name || child?.title || child?.description || '—',
            amount: moneyValue(child?.amount ?? child),
            asMoney: true,
            children: extractChildren(child),
        }))
        .filter((child) => child.amount !== 0 || child.children.length);
};

const detailRows = (items) =>
    items.filter((item) => item.amount !== 0 || item.children?.length);

const BalanceReport = () => {
    const cached = useMemo(readCache, []);
    const [report, setReport] = useState(cached);
    const [updatedAt, setUpdatedAt] = useState(cached?.updatedAt || null);
    const [loading, setLoading] = useState(!cached);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState({
        sales: true,
        returns: true,
        commission: true,
    });

    const loadReport = async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await axiosInstance.get('/v1/Statistics/ozon-balance');
            const nextReport = {
                date_from: data?.date_from,
                date_to: data?.date_to,
                cashflows: data?.cashflows || {},
                total: data?.total || {},
                updatedAt: new Date().toISOString(),
            };
            setReport(nextReport);
            setUpdatedAt(nextReport.updatedAt);
            try {
                localStorage.setItem(CACHE_KEY, JSON.stringify(nextReport));
            } catch {
                // Отчёт останется на экране, даже если кэш недоступен.
            }
        } catch (requestError) {
            setError(
                requestError.response?.data?.detail ||
                    requestError.response?.data?.message ||
                    'Не удалось загрузить отчёт о балансе'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReport();
    }, []);

    const view = useMemo(() => {
        const cashflows = report?.cashflows || {};
        const total = report?.total || {};
        const sales = cashflows.sales || {};
        const returns = cashflows.returns || {};
        const salesAmount = moneyValue(sales.amount);
        const returnsAmount = moneyValue(returns.amount);
        const salesDetails = amountDetails(sales);
        const returnsDetails = amountDetails(returns);
        const commission = moneyValue(sales.fee) + moneyValue(returns.fee);
        const rawServices = cashflows.services;
        const serviceList = Array.isArray(rawServices)
            ? rawServices
            : rawServices && typeof rawServices === 'object'
              ? Object.entries(rawServices).map(([name, item]) =>
                    item && typeof item === 'object'
                        ? { name: item.name || name, ...item }
                        : { name, amount: item }
                )
              : [];
        const services = serviceList
            .map((item, index) => ({
                key: `service-${item?.name || index}`,
                name: item?.name || 'Услуга',
                amount: moneyValue(item?.amount ?? item),
                children: extractChildren(item),
            }))
            .filter((item) => item.amount !== 0 || item.children.length);
        const compensation = services
            .filter((item) => /компенсац/i.test(item.name))
            .reduce((sum, item) => sum + item.amount, 0);
        const serviceFines = services
            .filter((item) => !/компенсац/i.test(item.name))
            .reduce((sum, item) => sum + item.amount, 0);
        const reserved =
            moneyValue(total.reserved) ||
            moneyValue(total.reserved_amount) ||
            moneyValue(total.hold) ||
            null;

        return {
            opening: moneyValue(total.opening_balance),
            closing: moneyValue(total.closing_balance),
            accrued: moneyValue(total.accrued),
            paid: (Array.isArray(total.payments) ? total.payments : []).reduce(
                (sum, item) => sum + moneyValue(item),
                0
            ),
            reserved: reserved || null,
            salesAmount,
            returnsAmount,
            salesReturns: salesAmount + returnsAmount,
            salesDetails,
            returnsDetails,
            revenue: salesDetails.revenue + returnsDetails.revenue,
            points: salesDetails.points + returnsDetails.points,
            partners: salesDetails.partners + returnsDetails.partners,
            commission,
            serviceFines,
            compensation,
            accruals: commission + serviceFines + compensation,
            services,
        };
    }, [report]);

    const percentOfSales = (amount) => {
        if (!view.salesAmount) return null;
        return `${((Math.abs(amount) / Math.abs(view.salesAmount)) * 100).toLocaleString(
            'ru-RU',
            { minimumFractionDigits: 1, maximumFractionDigits: 1 }
        )}%`;
    };

    const toggleRow = (key) => {
        setExpanded((current) => ({
            ...current,
            [key]: !(current[key] ?? true),
        }));
    };

    const rows = [
        {
            key: 'sales',
            label: 'Продажи',
            amount: view.salesAmount,
            children: detailRows([
                { name: 'Выручка', amount: view.salesDetails.revenue, asMoney: true },
                { name: 'Баллы за скидки', amount: view.salesDetails.points, asMoney: false },
                {
                    name: 'Программы партнёров',
                    amount: view.salesDetails.partners,
                    asMoney: true,
                },
            ]),
        },
        {
            key: 'returns',
            label: 'Возвраты',
            amount: view.returnsAmount,
            children: detailRows([
                { name: 'Возврат выручки', amount: view.returnsDetails.revenue, asMoney: true },
                { name: 'Баллы за скидки', amount: view.returnsDetails.points, asMoney: false },
                {
                    name: 'Программы партнёров',
                    amount: view.returnsDetails.partners,
                    asMoney: true,
                },
            ]),
        },
        {
            key: 'commission',
            label: 'Вознаграждение Ozon',
            amount: view.commission,
            percent: percentOfSales(view.commission),
            children: detailRows([
                {
                    name: 'Вознаграждение за продажу',
                    amount: moneyValue(report?.cashflows?.sales?.fee),
                    asMoney: true,
                },
                {
                    name: 'Возврат вознаграждения',
                    amount: moneyValue(report?.cashflows?.returns?.fee),
                    asMoney: true,
                },
            ]),
        },
        ...view.services.map((item) => ({
            key: item.key,
            label: item.name,
            amount: item.amount,
            percent: percentOfSales(item.amount),
            children: item.children,
        })),
    ];

    return (
        <section className="balance-report">
            <header className="balance-report__header">
                <div>
                    <h2>Баланс</h2>
                    <span>
                        {formatPeriod(report?.date_from, report?.date_to)}
                        {updatedAt &&
                            ` · обновлено ${new Date(updatedAt).toLocaleTimeString('ru-RU', {
                                hour: '2-digit',
                                minute: '2-digit',
                            })}`}
                    </span>
                </div>
                <button
                    type="button"
                    className="balance-report__reload"
                    onClick={loadReport}
                    disabled={loading}
                    aria-label="Обновить отчёт о балансе"
                >
                    <ReloadOutlined spin={loading} />
                </button>
            </header>

            {error && !report ? (
                <div className="balance-report__error">{error}</div>
            ) : loading && !report ? (
                <div className="balance-report__loading">Загружаем отчёт о балансе…</div>
            ) : (
                <>
                    {error && <div className="balance-report__stale-error">{error}</div>}

                    <div className="balance-summary">
                        <div className="balance-summary__main">
                            <span>Основной баланс</span>
                            <strong>{formatMoney(view.closing)}</strong>
                            {view.reserved != null && (
                                <p>
                                    <InfoCircleOutlined />
                                    Зарезервировано {formatMoney(view.reserved)}
                                </p>
                            )}
                        </div>
                        <dl className="balance-summary__rows">
                            <div>
                                <dt>На начало периода</dt>
                                <dd>{formatMoney(view.opening)}</dd>
                            </div>
                            <div>
                                <dt>Начислено за период</dt>
                                <dd>{formatMoney(view.accrued)}</dd>
                            </div>
                            <div>
                                <dt>Выплачено за период</dt>
                                <dd>{formatMoney(view.paid)}</dd>
                            </div>
                        </dl>
                    </div>

                    <div className="balance-cashflow">
                        <div className="balance-cashflow__cards">
                            <article>
                                <span>Продажи и возвраты</span>
                                <strong>{formatMoney(view.salesReturns)}</strong>
                                <ul>
                                    <li>
                                        <span>Выручка</span>
                                        <b>{formatMoney(view.revenue)}</b>
                                    </li>
                                    <li>
                                        <span>Баллы за скидки</span>
                                        <b>{formatPoints(view.points)}</b>
                                    </li>
                                    <li>
                                        <span>Программы партнёров</span>
                                        <b>{formatMoney(view.partners)}</b>
                                    </li>
                                </ul>
                            </article>
                            <article>
                                <span>Начисления</span>
                                <strong>{formatMoney(view.accruals)}</strong>
                                <ul>
                                    <li>
                                        <span>Вознаграждение Ozon</span>
                                        <b>{formatMoney(view.commission)}</b>
                                    </li>
                                    <li>
                                        <span>Услуги и штрафы</span>
                                        <b>{formatMoney(view.serviceFines)}</b>
                                    </li>
                                    <li>
                                        <span>Другие начисления</span>
                                        <b>{formatMoney(view.compensation)}</b>
                                    </li>
                                </ul>
                            </article>
                        </div>

                        <div className="balance-tree">
                            {rows.map((row) => {
                                const children = row.children || [];
                                const canExpand = children.length > 0;
                                const isOpen = canExpand && (expanded[row.key] ?? true);
                                return (
                                    <div
                                        key={row.key}
                                        className={`balance-tree__group${
                                            isOpen ? ' balance-tree__group--open' : ''
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            className="balance-tree__parent"
                                            onClick={() => canExpand && toggleRow(row.key)}
                                            disabled={!canExpand}
                                        >
                                            <RightOutlined />
                                            <span>{row.label}</span>
                                            <div>
                                                <b>{formatMoney(row.amount)}</b>
                                                {row.percent && <em>{row.percent}</em>}
                                            </div>
                                        </button>
                                        {isOpen && (
                                            <ul className="balance-tree__children">
                                                {children.map((child) => (
                                                    <li key={child.name}>
                                                        <span>{child.name}</span>
                                                        <b>
                                                            {child.asMoney === false
                                                                ? formatPoints(child.amount)
                                                                : formatMoney(child.amount)}
                                                        </b>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="balance-cashflow__total">
                            <span>Всего к выплате за период</span>
                            <strong>{formatMoney(view.accrued)}</strong>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export default BalanceReport;
